import { scrubSecretUrls } from './maskSecretPath'
import { redactSecrets } from './redactSecrets'

// Sentry noise filtering. Extracted out of index.js so it can be unit-tested — QA
// (2026-07-20) found the conditional half had been inert since it shipped, which no test
// could have caught while the logic lived inside a module whose import boots the whole app.
//
// Every pattern below is grounded in real events pulled from the vlg-admin Sentry project,
// not in guesses about what the messages look like.

// ALWAYS noise regardless of stack — browser-extension messaging and offline/ad-blocked
// fetch aborts never originate in our code, so drop them unconditionally via ignoreErrors.
export const SENTRY_ALWAYS_NOISE = [
  /Invalid call to runtime\.sendMessage/,
  'Load failed',
  'NetworkError: A network error occurred.',
]

// THIRD-PARTY SIGNATURES: messages precise enough to be noise by construction, dropped in
// beforeSend regardless of the stack.
//
// react-dom serialising a DOM node that already carries a React fiber. Sampled 12 of the 42
// events on this issue: 12/12 carry a `__reactFiber$…` property in the message and 0 have a
// single frame in our own code (all resolve to node_modules/react-dom or <anonymous>). The
// starting constructor is NOT stable — 8 SVGPatternElement, 4 HTMLAnchorElement — so the
// fiber marker is the discriminator, not the element type. Requiring it keeps a genuine
// circular-structure error on one of our own plain objects reportable.
//
// This cannot live in the conditional list below: at beforeSend time the react-dom frames
// are still un-sourcemapped bundle URLs on our own origin, so any frame-based test counts
// them as app code and never drops the event.
export const SENTRY_THIRD_PARTY_SIGNATURES = [
  /Converting circular structure to JSON[\s\S]*__reactFiber/,
]

// CONDITIONAL noise: third-party failures whose message could, in theory, also come from our
// own code. Kept OUT of ignoreErrors (which drops unconditionally, before beforeSend runs)
// and dropped only when no frame points at our own bundle, so an app-code variant still
// gets reported. The observed occurrence (iOS/DuckDuckGo WebAuthn on /success/signing)
// arrives with zero frames.
export const SENTRY_CONDITIONAL_NOISE = [
  "undefined is not an object (evaluating 'data.credentials')",
]

const matches = (patterns, message) => {
  if (!message) return false
  return patterns.some((pattern) =>
    pattern instanceof RegExp ? pattern.test(message) : message.includes(pattern)
  )
}

export const matchesThirdPartySignature = (message) => matches(SENTRY_THIRD_PARTY_SIGNATURES, message)
export const matchesConditionalNoise = (message) => matches(SENTRY_CONDITIONAL_NOISE, message)

// Is this stack frame our own bundle?
//
// This used to test `frame.in_app`, which QA proved is useless in the browser: @sentry/browser
// hardcodes `in_app: true` on every frame it parses (build/npm/esm/stack-parsers.js — "All
// browser frames are considered in_app"), so hasNoAppFrame() could only ever be true for an
// event with no frames at all. Frame URL is the signal that actually separates them: our
// bundle is served from the page's own origin, while injected extension code, native
// callbacks and third-party CDN scripts are not.
export const isAppFrame = (frame, origin) => {
  const url = frame?.abs_path || frame?.filename || ''
  if (!url) return false
  // chrome-extension:, moz-extension:, safari-web-extension: …
  if (/^[a-z-]+extension:/i.test(url)) return false
  if (url === '<anonymous>' || url === '[native code]') return false
  try {
    return new URL(url, origin).origin === origin
  } catch (error) {
    return false
  }
}

// True when nothing in the event's stack comes from our own bundle, across every exception
// value — a strong signal the error originated entirely in injected/third-party code, or
// has no stack at all.
export const hasNoAppFrame = (event, origin) => {
  const values = event?.exception?.values
  if (!values || values.length === 0) return true
  return values.every((value) => {
    const frames = value?.stacktrace?.frames
    if (!frames || frames.length === 0) return true
    return !frames.some((frame) => isAppFrame(frame, origin))
  })
}

export const shouldDropEvent = (event, hint, origin) => {
  const message =
    event?.message ||
    hint?.originalException?.message ||
    event?.exception?.values?.[0]?.value

  if (matchesThirdPartySignature(message)) return true
  return matchesConditionalNoise(message) && hasNoAppFrame(event, origin)
}

// `origin` is injected rather than read from window here so the filter stays a pure
// function and the tests don't have to fight jsdom's read-only location.
// scrubSecretUrls cleans the fields it names. redactSecrets is the net under it: it decides
// from the value, so a field nobody listed is still covered. An unfinished walk drops the
// payload rather than shipping a partly scrubbed one.
export const createBeforeSend = (origin) => (event, hint) => {
  if (shouldDropEvent(event, hint, origin)) return null

  scrubSecretUrls(event)
  const { event: redacted, complete } = redactSecrets(event)

  return complete ? redacted : null
}

export const beforeBreadcrumb = (breadcrumb) => {
  scrubSecretUrls({ breadcrumbs: [breadcrumb] })
  const { complete } = redactSecrets(breadcrumb)

  return complete ? breadcrumb : null
}
